/**
 * Utility functions for common operations
 * Provides reusable helper functions across the application
 */

import { CONFIG } from './config.js';

/**
 * Debounce function to limit the rate of function execution
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @param {boolean} immediate - Execute immediately on first call
 * @returns {Function} Debounced function
 */
export function debounce(func, wait, immediate = false) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            timeout = null;
            if (!immediate) func(...args);
        };
        const callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) func(...args);
    };
}

/**
 * Throttle function to limit the rate of function execution
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} Throttled function
 */
export function throttle(func, limit) {
    let inThrottle;
    return function executedFunction(...args) {
        if (!inThrottle) {
            func.apply(this, args);
            inThrottle = true;
            setTimeout(() => {
                inThrottle = false;
            }, limit);
        }
    };
}

/**
 * Deep clone an object
 * @param {*} obj - Object to clone
 * @returns {*} Cloned object
 */
export function deepClone(obj) {
    if (obj === null || typeof obj !== 'object') return obj;
    if (obj instanceof Date) return new Date(obj.getTime());
    if (obj instanceof Array) return obj.map(item => deepClone(item));
    if (typeof obj === 'object') {
        const clonedObj = {};
        for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                clonedObj[key] = deepClone(obj[key]);
            }
        }
        return clonedObj;
    }
}

/**
 * Check if a value is empty (null, undefined, empty string, empty array, empty object)
 * @param {*} value - Value to check
 * @returns {boolean} True if value is empty
 */
export function isEmpty(value) {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return value.trim() === '';
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') return Object.keys(value).length === 0;
    return false;
}

/**
 * Generate a unique ID
 * @param {string} prefix - Optional prefix for the ID
 * @returns {string} Unique ID
 */
export function generateId(prefix = 'id') {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Format date to Czech format
 * @param {Date|string} date - Date to format
 * @param {string} format - Format string (default: CONFIG.KENDO.DATE_FORMAT)
 * @returns {string} Formatted date
 */
export function formatDate(date, format = CONFIG.KENDO.DATE_FORMAT) {
    if (!date) return '';

    const d = new Date(date);
    if (isNaN(d.getTime())) return '';

    const day = d.getDate();
    const month = d.getMonth() + 1;
    const year = d.getFullYear();

    return format.replace('d', day).replace('M', month).replace('yyyy', year);
}

/**
 * Parse query string parameters
 * @param {string} queryString - Query string (default: window.location.search)
 * @returns {Object} Parsed parameters
 */
export function parseQueryString(queryString = window.location.search) {
    const params = {};
    const urlParams = new URLSearchParams(queryString);

    for (const [key, value] of urlParams) {
        params[key] = value;
    }

    return params;
}

/**
 * Build query string from object
 * @param {Object} params - Parameters object
 * @returns {string} Query string
 */
export function buildQueryString(params) {
    const urlParams = new URLSearchParams();

    Object.keys(params).forEach(key => {
        if (params[key] !== null && params[key] !== undefined) {
            urlParams.append(key, params[key]);
        }
    });

    return urlParams.toString();
}

/**
 * Wait for a specified amount of time
 * @param {number} ms - Milliseconds to wait
 * @returns {Promise} Promise that resolves after the specified time
 */
export function wait(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Check if element is visible in viewport
 * @param {HTMLElement} element - Element to check
 * @returns {boolean} True if element is visible
 */
export function isElementInViewport(element) {
    if (!element) return false;

    const rect = element.getBoundingClientRect();
    return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
}

/**
 * Scroll element into view smoothly
 * @param {HTMLElement|string} element - Element or selector
 * @param {Object} options - Scroll options
 */
export function scrollIntoView(element, options = {}) {
    const el = typeof element === 'string' ? document.querySelector(element) : element;
    if (el) {
        el.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            ...options
        });
    }
}

/**
 * Get element by selector with error handling
 * @param {string} selector - CSS selector
 * @param {HTMLElement} context - Context element (default: document)
 * @returns {HTMLElement|null} Found element or null
 */
export function safeQuerySelector(selector, context = document) {
    try {
        return context.querySelector(selector);
    } catch (error) {
        // eslint-disable-next-line no-console
        console.warn(`Invalid selector: ${selector}`, error);
        return null;
    }
}

/**
 * Get all elements by selector with error handling
 * @param {string} selector - CSS selector
 * @param {HTMLElement} context - Context element (default: document)
 * @returns {NodeList} Found elements
 */
export function safeQuerySelectorAll(selector, context = document) {
    try {
        return context.querySelectorAll(selector);
    } catch (error) {
        // eslint-disable-next-line no-console
        console.warn(`Invalid selector: ${selector}`, error);
        return [];
    }
}

/**
 * Add event listener with automatic cleanup
 * @param {HTMLElement} element - Element to attach listener to
 * @param {string} event - Event name
 * @param {Function} handler - Event handler
 * @param {Object} options - Event options
 * @returns {Function} Cleanup function
 */
export function addEventListenerWithCleanup(element, event, handler, options = {}) {
    if (!element) return () => {};

    element.addEventListener(event, handler, options);

    return () => {
        element.removeEventListener(event, handler, options);
    };
}

/**
 * Create a promise that resolves when a condition is met
 * @param {Function} condition - Function that returns true when condition is met
 * @param {number} timeout - Timeout in milliseconds (default: CONFIG.UTILS.WAIT_FOR_CONDITION.DEFAULT_TIMEOUT_MS)
 * @param {number} interval - Check interval in milliseconds (default: CONFIG.UTILS.WAIT_FOR_CONDITION.DEFAULT_INTERVAL_MS)
 * @returns {Promise} Promise that resolves when condition is met
 */
export function waitForCondition(
    condition,
    timeout = CONFIG.UTILS.WAIT_FOR_CONDITION.DEFAULT_TIMEOUT_MS,
    interval = CONFIG.UTILS.WAIT_FOR_CONDITION.DEFAULT_INTERVAL_MS
) {
    return new Promise((resolve, reject) => {
        const startTime = Date.now();

        const check = () => {
            if (condition()) {
                resolve();
            } else if (Date.now() - startTime > timeout) {
                reject(new Error('Condition timeout'));
            } else {
                setTimeout(check, interval);
            }
        };

        check();
    });
}

export default {
    debounce,
    throttle,
    deepClone,
    isEmpty,
    generateId,
    formatDate,
    parseQueryString,
    buildQueryString,
    wait,
    isElementInViewport,
    scrollIntoView,
    safeQuerySelector,
    safeQuerySelectorAll,
    addEventListenerWithCleanup,
    waitForCondition
};
